/**
 * Dumb-компонент полей формы «Проверка».
 * Ничего не знает об API и глобальном состоянии — только props + колбэки.
 */
import { useRef } from 'react';
import { UserRecipientCombo, type RecipientUserItem } from '@/components/UserRecipientCombo/UserRecipientCombo';
import { SelectDropdown } from '@/components/ui/SelectDropdown/SelectDropdown';
import { DateAutofillInput } from '@/components/ui/DateAutofillInput/DateAutofillInput';
import { Input } from '@/components/ui/Input/Input';
import { Textarea } from '@/components/ui/Textarea/Textarea';
import { TimeInput } from '@/components/ui/TimeInput/TimeInput';
import { DepartmentCombo } from '@/components/DepartmentCombo/DepartmentCombo';
import { EditionCombo } from '@/components/EditionCombo/EditionCombo';
import { ContractorCombo, type ContractorValueItem } from '@/components/ContractorCombo/ContractorCombo';
import type { DepartmentValueItem } from '@/components/DepartmentCombo/DepartmentCombo';
import type { EditionValueItem } from '@/components/EditionCombo/EditionCombo';
import {
  REVIEW_CHECK_TYPES,
  REVIEW_CHECK_TYPE_LABELS,
  REVIEW_CHECK_TYPE_PLACEHOLDER,
  REVIEW_NOT_SPECIFIED_LABEL,
} from '@/utils/reviewCheckType';
import type { ReviewCheckType } from '@/api/client';

export interface ReviewCheckEmailFieldsProps {
  recipients: RecipientUserItem[];
  onRecipientsChange: (items: RecipientUserItem[]) => void;
  ccUsers: RecipientUserItem[];
  onCcUsersChange: (items: RecipientUserItem[]) => void;
  reviewType: string;
  onReviewTypeChange: (value: string) => void;
  reviewDeadline: string;
  onReviewDeadlineChange: (value: string) => void;
  studioContractor: ContractorValueItem | null;
  onStudioContractorChange: (item: ContractorValueItem | null) => void;
  asta: string;
  onAstaChange: (value: string) => void;
  departmentValue: DepartmentValueItem | null;
  onDepartmentChange: (value: DepartmentValueItem | null) => void;
  editionValue: EditionValueItem | null;
  onEditionChange: (value: EditionValueItem | null) => void;
  time: string;
  onTimeChange: (value: string) => void;
  tracksCount: string;
  onTracksCountChange: (value: string) => void;
  sizeValue: string;
  onSizeValueChange: (value: string) => void;
  commentNote: string;
  onCommentNoteChange: (value: string) => void;
  onCreateError: (e: Error) => void;
  disabled?: boolean;
}

export function ReviewCheckEmailFields({
  recipients,
  onRecipientsChange,
  ccUsers,
  onCcUsersChange,
  reviewType,
  onReviewTypeChange,
  reviewDeadline,
  onReviewDeadlineChange,
  studioContractor,
  onStudioContractorChange,
  asta,
  onAstaChange,
  departmentValue,
  onDepartmentChange,
  editionValue,
  onEditionChange,
  time,
  onTimeChange,
  tracksCount,
  onTracksCountChange,
  sizeValue,
  onSizeValueChange,
  commentNote,
  onCommentNoteChange,
  onCreateError,
  disabled,
}: ReviewCheckEmailFieldsProps) {
  const recipientRef = useRef<HTMLDivElement>(null);
  const ccComboRef = useRef<HTMLDivElement>(null);
  const studioComboRef = useRef<HTMLDivElement>(null);
  const departmentComboRef = useRef<HTMLDivElement>(null);
  const editionComboRef = useRef<HTMLDivElement>(null);

  return (
    <div className="review-check-email-modal__fields">
      <div className="review-check-email-modal__field">
        <UserRecipientCombo
          value={recipients}
          onChange={onRecipientsChange}
          placeholder="Получатель"
          containerRef={recipientRef}
          disabled={disabled}
        />
      </div>
      <div className="review-check-email-modal__field">
        <UserRecipientCombo
          value={ccUsers}
          onChange={onCcUsersChange}
          placeholder="Копия"
          containerRef={ccComboRef}
          disabled={disabled}
        />
      </div>
      <div className="review-check-email-modal__row review-check-email-modal__row_cols-2">
        <div className="review-check-email-modal__field">
          <SelectDropdown
            fullWidth
            value={reviewType}
            onChange={onReviewTypeChange}
            placeholder={REVIEW_CHECK_TYPE_PLACEHOLDER}
            options={[
              { value: '', label: REVIEW_NOT_SPECIFIED_LABEL },
              ...REVIEW_CHECK_TYPES.map((k) => ({ value: k, label: REVIEW_CHECK_TYPE_LABELS[k] })),
            ]}
            renderValue={() => {
              const k = reviewType.trim();
              if (!k) {
                return (
                  <span className="select-dropdown__value select-dropdown__placeholder">
                    {REVIEW_CHECK_TYPE_PLACEHOLDER}
                  </span>
                );
              }
              const lbl = REVIEW_CHECK_TYPE_LABELS[k as ReviewCheckType];
              return <span className="select-dropdown__value">{lbl ?? k}</span>;
            }}
            disabled={disabled}
          />
        </div>
        <div className="review-check-email-modal__field">
          <DateAutofillInput
            value={reviewDeadline}
            onChange={onReviewDeadlineChange}
            placeholder="Дедлайн проверки"
            fullWidth
            disabled={disabled}
          />
        </div>
      </div>
      <div className="review-check-email-modal__field">
        <ContractorCombo
          value={studioContractor}
          onChange={onStudioContractorChange}
          contractorType="studio"
          placeholder="Студия"
          containerRef={studioComboRef}
          disabled={disabled}
        />
      </div>
      <div className="review-check-email-modal__field">
        <Input
          value={asta}
          onChange={(e) => onAstaChange(e.target.value)}
          placeholder="ASTA"
          fullWidth
          disabled={disabled}
        />
      </div>
      <div className="review-check-email-modal__row review-check-email-modal__row_cols-2">
        <div className="review-check-email-modal__field">
          <DepartmentCombo
            value={departmentValue}
            onChange={onDepartmentChange}
            placeholder="Подразделение"
            containerRef={departmentComboRef}
            onCreateError={onCreateError}
            disabled={disabled}
          />
        </div>
        <div className="review-check-email-modal__field">
          <EditionCombo
            value={editionValue}
            onChange={onEditionChange}
            departmentId={departmentValue?.id ?? null}
            placeholder="Редакция"
            containerRef={editionComboRef}
            onCreateError={onCreateError}
            disabled={disabled}
          />
        </div>
      </div>
      <div className="review-check-email-modal__row review-check-email-modal__row_cols-3">
        <div className="review-check-email-modal__field">
          <TimeInput value={time} onChange={onTimeChange} placeholder="00:00:00" fullWidth disabled={disabled} />
        </div>
        <div className="review-check-email-modal__field">
          <Input
            value={tracksCount}
            onChange={(e) => onTracksCountChange(e.target.value.replace(/[^\d]/g, ''))}
            placeholder="Количество треков"
            fullWidth
            disabled={disabled}
          />
        </div>
        <div className="review-check-email-modal__field">
          <Input
            value={sizeValue}
            onChange={(e) => {
              const v = e.target.value.replace(',', '.').replace(/[^\d.]/g, '');
              const parts = v.split('.');
              if (parts.length <= 1) onSizeValueChange(v);
              else onSizeValueChange(parts[0] + '.' + parts.slice(1).join(''));
            }}
            placeholder="Размер (ГБ)"
            fullWidth
            disabled={disabled}
          />
        </div>
      </div>
      <div className="review-check-email-modal__field">
        <Textarea
          value={commentNote}
          onChange={(e) => onCommentNoteChange(e.target.value)}
          placeholder="Комментарий к письму"
          fullWidth
          minRows={1}
          maxRows={40}
          inputClassName="review-check-email-modal__comment-input"
          disabled={disabled}
        />
      </div>
    </div>
  );
}
